import {
  Eye,
  Flame,
  Network as NetworkIcon,
  Play,
  RefreshCw,
  TriangleAlert,
  type LucideIcon,
} from 'lucide-react';
import { Link } from 'react-router';
import type { ChangeAction, RunChanges, RunKind, RunSummary } from '../api/types';
import { actionPresentation, runStatusPresentation } from '../components/presentation';
import { StatusBadge } from '../components/ui';
import { durationMs, formatDateTime, formatDuration, formatRelative } from '../lib/format';
import { strings } from '../strings';
import { useCardTable } from '../lib/cardTable';
import { WaitingText } from '../networks/components';
import { isNetworkSummary, retryWords } from '../networks/runs/runHelpers';
import { grantPendingPresentation, isGrantPending, networkRunLabel, useNetworking } from '../networks/shared';

const s = strings.runsList;
const n = strings.networks;

export const RUN_KIND_ICON: Record<RunKind, LucideIcon> = {
  plan: Eye,
  apply: Play,
  destroy: Flame,
  refresh: RefreshCw,
};

const GLYPH: Record<Exclude<ChangeAction, 'no-change'>, string> = {
  create: '+',
  update: '~',
  replace: '±',
  delete: '−',
};

/**
 * Change counts of a run summary (spec 6.9): tone badges with glyph and number, zeros left out, "No changes"
 * when all are zero; "Unknown" for a completed plan or apply whose counts weren't recorded; "–" otherwise.
 */
export function RunChangesCell({ run }: { run: Pick<RunSummary, 'changes' | 'kind' | 'status'> }) {
  const changes: RunChanges | null = run.changes;
  if (!changes) {
    if ((run.kind === 'plan' || run.kind === 'apply') && run.status === 'completed') {
      return (
        <span className="text-fg-muted" title={s.unknownTitle}>
          {s.unknown}
        </span>
      );
    }
    return <span className="text-fg-muted">{strings.common.dash}</span>;
  }
  const actions = (['create', 'update', 'replace', 'delete'] as const).filter(
    (action) => changes[action] > 0,
  );
  if (actions.length === 0) return <span className="text-fg-muted">{s.noChanges}</span>;
  return (
    <span className="flex flex-wrap gap-1">
      {actions.map((action) => {
        const label = s.changeCount(actionPresentation[action].label, changes[action]);
        return (
          <span
            key={action}
            className={`badge tone-${actionPresentation[action].tone} font-mono`}
            title={label}
            data-change={action}
          >
            <span aria-hidden>
              {GLYPH[action]}
              {changes[action]}
            </span>
            <span className="sr-only">{label}</span>
          </span>
        );
      })}
    </span>
  );
}

/** The spoke changes of an attached deployment's run (feature spec 17): "network +5 −2". */
export function NetworkChangesCell({ run }: { run: Pick<RunSummary, 'networkChanges'> }) {
  const changes = run.networkChanges;
  if (!changes) return null;
  const actions = (['create', 'update', 'replace', 'delete'] as const).filter(
    (action) => changes[action] > 0,
  );
  if (actions.length === 0) return null;
  return (
    <span className="flex flex-wrap items-center gap-1" data-testid="network-changes">
      <span className="text-[12px] text-fg-muted">{n.runs.networkChanges}</span>
      {actions.map((action) => {
        const label = n.runs.networkChangeCount(actionPresentation[action].label, changes[action]);
        return (
          <span
            key={action}
            className={`badge tone-${actionPresentation[action].tone} font-mono`}
            title={label}
            data-change={action}
          >
            <span aria-hidden>
              {GLYPH[action]}
              {changes[action]}
            </span>
            <span className="sr-only">{label}</span>
          </span>
        );
      })}
    </span>
  );
}

/**
 * A run's status badge (spec 6.9): a connect run that failed with `NETWORK_GRANT_PENDING` reads "Failed · retrying at
 * <time> (waiting for permissions)" in the neutral tone, never as a red failure (6.15.17).
 */
export function RunStatusBadge({ run, retryAt }: { run: RunSummary; retryAt?: string | null }) {
  if (isGrantPending(run))
    return (
      <StatusBadge
        presentation={grantPendingPresentation(n.waiting.grantPendingFailed(retryWords(retryAt)))}
      />
    );
  return <StatusBadge presentation={runStatusPresentation[run.status]} />;
}

/** The scheduled retry of a connect run that failed with `NETWORK_GRANT_PENDING`, when the list has it. */
function pendingRetryOf(run: RunSummary, runs: readonly RunSummary[]): string | null {
  if (!isGrantPending(run)) return null;
  return (
    runs.find(
      (other) =>
        other.status === 'pending' &&
        other.attachmentDeploymentId === run.attachmentDeploymentId &&
        other.waitingFor?.reason === 'not-before',
    )?.waitingFor?.until ?? null
  );
}

function RunStatusCell({ run, retryAt }: { run: RunSummary; retryAt: string | null }) {
  const active = run.status === 'pending' || run.status === 'running';
  const progress = run.progress && run.progress.total > 0 ? run.progress : null;
  const warnings = run.warnings ?? [];
  return (
    <span className="flex flex-wrap items-center gap-1.5">
      <RunStatusBadge run={run} retryAt={retryAt} />
      {active && progress && (
        <span className="font-mono text-[12px] text-fg-muted">
          {strings.shell.activeRuns.progress(progress.completed, progress.total)}
        </span>
      )}
      {warnings.length > 0 && (
        <span className="badge tone-warning" title={warnings.map((w) => w.code).join(', ')}>
          <TriangleAlert aria-hidden className="size-3.5" />
          {s.warnings(warnings.length)}
        </span>
      )}
      {run.status === 'pending' && run.waitingFor && (
        <span className="block w-full text-[12px] text-fg-muted" data-testid="run-waiting">
          <WaitingText waitingFor={run.waitingFor} />
        </span>
      )}
    </span>
  );
}

function TriggeredBy({ run }: { run: RunSummary }) {
  if (run.triggeredBy === 'attachment') return <>{n.runLabels.nebisysAttachment}</>;
  if (run.triggeredBy === 'rollback' && run.parentRunId) {
    return (
      <Link className="link relative z-[1]" to={`/runs/${run.parentRunId}`}>
        {s.rollbackOf} {s.parentRun}
      </Link>
    );
  }
  return <>{run.createdBy}</>;
}

/**
 * A table of run summaries (spec 6.9), used by the runs list and the deployment page. `showDeployment` adds
 * the Deployment column (with the blueprint below it).
 */
export function RunSummaryTable({
  runs,
  showDeployment,
  labelledBy,
  deploymentName,
  pendingRuns,
}: {
  runs: RunSummary[];
  showDeployment: boolean;
  labelledBy?: string;
  /** Names attachment runs on a deployment's own pages ("Connect dev"), 6.15.11. */
  deploymentName?: string | null;
  /** Pending runs to look up a grant-pending connect run's retry in (the active runs), else `runs`. */
  pendingRuns?: readonly RunSummary[];
}) {
  const cardTable = useCardTable();
  // Feature spec 17: with networking on, the Deployment column is Target (a deployment or a network).
  const { enabled: networking } = useNetworking();
  return (
    <div className="overflow-x-auto rounded-lg border border-line bg-surface">
      <table ref={cardTable} className="table table-cards" aria-labelledby={labelledBy}>
        <thead>
          <tr>
            <th scope="col">{s.run}</th>
            {showDeployment && <th scope="col">{networking ? n.runs.target : s.deployment}</th>}
            <th scope="col">{s.status}</th>
            <th scope="col">{s.triggeredBy}</th>
            <th scope="col">{s.version}</th>
            <th scope="col">{s.started}</th>
            <th scope="col">{s.duration}</th>
            <th scope="col">{s.changes}</th>
          </tr>
        </thead>
        <tbody>
          {runs.map((run) => {
            const Icon = RUN_KIND_ICON[run.kind];
            const network = isNetworkSummary(run);
            return (
              <tr key={run.id} data-run-id={run.id}>
                <td>
                  <span className="flex flex-wrap items-center gap-1.5">
                    <Icon aria-hidden className="size-4 shrink-0 text-fg-muted" />
                    <Link className="link font-medium" to={`/runs/${run.id}`}>
                      {run.scope === 'attachment'
                        ? networkRunLabel(run, deploymentName)
                        : strings.status.runKind[run.kind]}
                    </Link>
                    {run.scope === 'vault' && <span className="badge tone-info">{s.vaultOnly}</span>}
                    {run.scope === 'attachment' && (
                      <span className="badge tone-info">{n.runLabels.attachment}</span>
                    )}
                    {run.scope === 'attachment' && run.parentRunId && (
                      <span className="block w-full text-[12px] text-fg-muted">
                        {n.runLabels.queuedAfter}{' '}
                        <Link className="link relative z-[1]" to={`/runs/${run.parentRunId}`}>
                          {n.runs.parentRun}
                        </Link>
                      </span>
                    )}
                  </span>
                </td>
                {showDeployment && network && (
                  <td>
                    <span className="flex items-center gap-1.5">
                      <NetworkIcon aria-hidden className="size-4 shrink-0 text-fg-muted" />
                      <Link className="link cell-link font-medium" to={`/networks/${run.networkId}`}>
                        {run.networkName ?? run.target?.name ?? n.nav.kind}
                      </Link>
                    </span>
                    <span className="block text-[12px] text-fg-muted">{n.runs.networkBelow}</span>
                  </td>
                )}
                {showDeployment && !network && (
                  <td>
                    {/* Stacked links keep the 24 px target size (WCAG 2.5.8). */}
                    <Link className="link cell-link font-medium" to={`/deployments/${run.deploymentId}`}>
                      {run.deploymentName}
                    </Link>
                    <span className="block text-[12px]">
                      <Link
                        className="cell-link text-fg-muted hover:underline"
                        to={`/blueprints/${run.blueprintId}`}
                      >
                        {run.blueprintName}
                      </Link>
                    </span>
                  </td>
                )}
                <td>
                  <RunStatusCell run={run} retryAt={pendingRetryOf(run, pendingRuns ?? runs)} />
                </td>
                <td>
                  <TriggeredBy run={run} />
                </td>
                <td>{run.blueprintVersion !== null ? `v${run.blueprintVersion}` : strings.common.dash}</td>
                <td title={formatDateTime(run.startedAt ?? run.createdAt)}>
                  {run.startedAt
                    ? formatRelative(run.startedAt)
                    : run.status === 'pending'
                      ? s.queued
                      : strings.common.dash}
                </td>
                <td>{formatDuration(durationMs(run.startedAt, run.finishedAt))}</td>
                <td>
                  <RunChangesCell run={run} />
                  <NetworkChangesCell run={run} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
